import { useEffect, useRef } from "react";

/*
 * useStepBackGuard — pure-JS back interception (NO native code).
 *
 * Works in the browser AND inside an Android/iOS WebView: the hardware /
 * gesture back button in a WebView triggers SPA history navigation (the
 * very reason "back" currently escapes a wizard route), so the same
 * `popstate` event we listen to here fires there too. Nothing on the
 * native side is required.
 *
 * Mechanism — keep a MARKED sentinel history entry above the current
 * route. On Back the browser pops the sentinel (same-origin → popstate,
 * no page load) and we decide:
 *   - canGoBack() true  → onBack()  (e.g. step--)  then re-arm
 *   - canGoBack() false → onExit()  (e.g. open an exit-confirm modal)
 * Either way we re-arm so the visible route stays put — Back can never
 * cross over to the previous (cross-origin Stripe) entry.
 *
 * Self-healing: host screens (e.g. GoLiveWizard) run ASYNC boot cleanups
 * that call history.replaceState to strip query params, which OVERWRITES
 * our top sentinel and would let Back escape. We tag the sentinel with a
 * `__backGuard` marker and re-arm on a short interval whenever the
 * marker is missing — so however/whenever a cleanup wipes it, the
 * sentinel is restored within a few hundred ms. After boot settles the
 * marker stays and the interval becomes a cheap no-op.
 */
export default function useStepBackGuard({
  canGoBack,
  onBack,
  onExit,
  active = true,
}) {
  const canGoBackRef = useRef(canGoBack);
  const onBackRef = useRef(onBack);
  const onExitRef = useRef(onExit);
  canGoBackRef.current = canGoBack;
  onBackRef.current = onBack;
  onExitRef.current = onExit;

  useEffect(() => {
    if (!active) return undefined;

    // Push a marked sentinel, preserving React Router's existing
    // history.state (idx/key/usr) so RR keeps routing correctly.
    const arm = () => {
      try {
        const prev = window.history.state || {};
        if (prev.__backGuard) return; // already armed on top
        window.history.pushState(
          { ...prev, __backGuard: true },
          "",
          window.location.href,
        );
      } catch {
        /* history unavailable — guard simply won't engage */
      }
    };

    const isArmed = () => {
      try {
        return !!(window.history.state && window.history.state.__backGuard);
      } catch {
        return false;
      }
    };

    arm();

    // Restore the sentinel whenever a boot cleanup (or anything else)
    // overwrites it. Cheap no-op once the marker is stably on top.
    const heal = setInterval(() => {
      if (!isArmed()) arm();
    }, 500);

    const onPop = () => {
      // The browser already popped our sentinel; re-arm so the visible
      // route stays put regardless of the decision below.
      arm();
      const can =
        typeof canGoBackRef.current === "function"
          ? canGoBackRef.current()
          : false;
      if (can) {
        onBackRef.current && onBackRef.current();
      } else {
        onExitRef.current && onExitRef.current();
      }
    };

    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      clearInterval(heal);
    };
  }, [active]);
}
